/**
 * Confirm dialog. Pass `body` to give context for what's being confirmed —
 * delete-confirms with no body just show a one-line title and feel risky.
 *
 * Props:
 *  - open                 boolean — if false, renders nothing
 *  - title                string  — required, large header text
 *  - body                 string | ReactNode — optional explanation under the title
 *  - confirmLabel         string  — default "Yes". Use "Delete", "Confirm", etc. for clarity
 *  - cancelLabel          string  — default "Cancel"
 *  - confirmDisabled      boolean — disable the confirm button (e.g. while saving)
 *  - confirmDestructive   boolean — default true. Red hover for delete-style. Set false
 *                                   for non-destructive confirms (e.g. "Mark match as ready")
 *  - onConfirm / onCancel function — required handlers
 */
import { useRef } from "react";
import useDialogDismiss from "../../hooks/useDialogDismiss";

export default function Modal({
  open,
  title,
  body,
  confirmLabel = "Yes",
  cancelLabel = "Cancel",
  confirmDisabled = false,
  confirmDestructive = true,
  onConfirm,
  onCancel,
}) {
  const panelRef = useRef(null);
  // 2026-06-08 — adds Esc / backdrop-click / hardware-back / focus-trap
  // to the generic confirm dialog. Closes via onCancel since this is
  // a confirm modal — Esc/backdrop = "I'm not going to confirm".
  useDialogDismiss({ open, onClose: onCancel, ref: panelRef });

  if (!open) return null;

  const confirmHover = confirmDestructive ? "hover:bg-red-600" : "hover:bg-button-primary";

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3 sm:p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="bg-bg-primary p-6 sm:p-10 md:p-12 w-full max-w-sm sm:max-w-md max-h-[90vh] overflow-y-auto rounded-lg flex flex-col gap-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="modal-title" className="text-center text-xl font-semibold">
          {title}
        </h3>
        {body && (
          <div className="text-center text-text-secondary text-sm whitespace-pre-line">
            {body}
          </div>
        )}
        <div className="flex justify-center gap-4 mt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 rounded text-text-primary hover:bg-bg-secondary transition-all duration-150"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            disabled={confirmDisabled}
            onClick={onConfirm}
            className={`bg-bg-secondary px-6 py-2 rounded ${confirmHover} transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
